import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { overpassApi } from "@/services/api-v2.service";
import type { OverpassStats, OverpassSettings } from "@/types/api-v2";
import {
  Globe,
  Settings,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Trash2,
  Database
} from "lucide-react";

/** Дефолтные public mirrors (согласовано с backend app.core.overpass_constants). */
const DEFAULT_PUBLIC_PRIMARY = "https://lz4.overpass-api.de/api/interpreter";
const DEFAULT_PUBLIC_BACKUPS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass.openstreetmap.ru/api/interpreter",
];

function defaultPublicBackupsExcluding(primary: string): string[] {
  const primaryN = primary.trim();
  const out: string[] = [];
  for (const url of DEFAULT_PUBLIC_BACKUPS) {
    if (url && url !== primaryN && !out.includes(url)) out.push(url);
  }
  if (DEFAULT_PUBLIC_PRIMARY !== primaryN && !out.includes(DEFAULT_PUBLIC_PRIMARY)) {
    out.unshift(DEFAULT_PUBLIC_PRIMARY);
  }
  return out;
}

export default function OverpassApiManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [settings, setSettings] = useState<OverpassSettings>({
    enabled: true,
    endpointMode: "local",
    primaryEndpoint: (import.meta.env.VITE_OVERPASS_URL as string) || 'http://localhost:8082/api/interpreter',
    localPrimaryEndpoint: (import.meta.env.VITE_OVERPASS_URL as string) || 'http://localhost:8082/api/interpreter',
    localBackupEndpoints: [],
    publicPrimaryEndpoint: DEFAULT_PUBLIC_PRIMARY,
    publicBackupEndpoints: defaultPublicBackupsExcluding(DEFAULT_PUBLIC_PRIMARY),
    backupEndpoints: defaultPublicBackupsExcluding(DEFAULT_PUBLIC_PRIMARY),
    timeoutSeconds: 30,
    retryAttempts: 3,
    rateLimitPerMinute: 10,
    useCompression: true,
    queryOptimization: true,
    cacheResults: true,
    cacheTtlMinutes: 60,
    maxQuerySize: 1000000,
    allowedTags: ['building', 'highway', 'landuse', 'boundary', 'natural', 'amenity'],
    customQueries: {
      buildings: '[out:json][timeout:25];\n(\n  way["building"]({bbox});\n  relation["building"]({bbox});\n);\nout geom;',
      roads: '[out:json][timeout:25];\n(\n  way["highway"]({bbox});\n);\nout geom;',
      landuse: '[out:json][timeout:25];\n(\n  way["landuse"]({bbox});\n  relation["landuse"]({bbox});\n);\nout geom;',
      boundaries: '[out:json][timeout:25];\n(\n  way["boundary"="administrative"]({bbox});\n  relation["boundary"="administrative"]({bbox});\n);\nout geom;'
    }
  });

  // Получение статистики Overpass API
  const { data: overpassStats, isLoading: isLoadingStats } = useQuery<OverpassStats>({
    queryKey: ['admin', 'overpass', 'stats'],
    queryFn: () => overpassApi.getStats(),
    refetchInterval: 30000,
  });

  // Получение настроек Overpass API
  const { data: currentSettings } = useQuery<OverpassSettings>({
    queryKey: ['admin', 'overpass', 'settings'],
    queryFn: () => overpassApi.getSettings(),
  });

  // Обновление локальных настроек при получении данных с сервера
  React.useEffect(() => {
    if (!currentSettings) return;
    const mode = currentSettings.endpointMode || "local";
    const backups =
      currentSettings.backupEndpoints?.length
        ? currentSettings.backupEndpoints
        : currentSettings.publicBackupEndpoints || [];
    if (mode === "public" && backups.length === 0) {
      const primary =
        currentSettings.publicPrimaryEndpoint ||
        currentSettings.primaryEndpoint ||
        DEFAULT_PUBLIC_PRIMARY;
      const filled = defaultPublicBackupsExcluding(primary);
      setSettings({
        ...currentSettings,
        primaryEndpoint: primary,
        publicPrimaryEndpoint: primary,
        backupEndpoints: filled,
        publicBackupEndpoints: filled,
      });
      return;
    }
    setSettings(currentSettings);
  }, [currentSettings]);

  // Мутация для сохранения настроек
  const saveSettingsMutation = useMutation({
    mutationFn: async (newSettings: OverpassSettings) => {
      return await overpassApi.updateSettings(newSettings);
    },
    onSuccess: () => {
      toast({
        title: "Настройки сохранены",
        description: "Настройки Overpass API успешно обновлены"
      });
      setIsSettingsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'overpass', 'settings'] });
    },
    onError: () => {
      toast({
        title: "Ошибка",
        description: "Не удалось сохранить настройки",
        variant: "destructive"
      });
    }
  });

  // Мутация для тестирования соединения
  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      return await overpassApi.testConnection();
    },
    onSuccess: (data) => {
      if (data.status === 'success') {
        const endpointHint = data.endpoint ? ` Эндпоинт: ${data.endpoint}.` : "";
        toast({
          title: "Соединение успешно",
          description:
            data.message ||
            `Overpass API доступен.${endpointHint} Время ответа: ${data.responseTime || 0}мс`,
        });
        // Обновляем статистику после успешного теста
        queryClient.invalidateQueries({ queryKey: ['admin', 'overpass', 'stats'] });
      } else {
        toast({
          title: "Ошибка соединения",
          description: data.message || "Не удалось подключиться к Overpass API",
          variant: "destructive"
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка соединения",
        description: error.message || "Не удалось подключиться к Overpass API",
        variant: "destructive"
      });
    }
  });

  // Мутация для очистки логов
  const cleanupLogsMutation = useMutation({
    mutationFn: async () => {
      return await overpassApi.cleanupLogs();
    },
    onSuccess: (data) => {
      toast({
        title: "Логи очищены",
        description: data.message || `Удалено ${data.deletedCount || 0} записей`
      });
      // Обновляем статистику после очистки
      queryClient.invalidateQueries({ queryKey: ['admin', 'overpass', 'stats'] });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось очистить логи",
        variant: "destructive"
      });
    }
  });

  const handleSaveSettings = () => {
    const mode = settings.endpointMode || "local";
    const toSave: OverpassSettings = {
      ...settings,
      endpointMode: mode,
    };
    // Зеркалим активные primary/backup в mode-specific поля (как на backend)
    if (mode === "public") {
      toSave.publicPrimaryEndpoint = settings.primaryEndpoint;
      toSave.publicBackupEndpoints = [...(settings.backupEndpoints || [])];
    } else {
      toSave.localPrimaryEndpoint = settings.primaryEndpoint;
      toSave.localBackupEndpoints = [...(settings.backupEndpoints || [])];
    }
    saveSettingsMutation.mutate(toSave);
  };

  const handleTestConnection = () => {
    setIsTestingConnection(true);
    testConnectionMutation.mutate();
    setTimeout(() => setIsTestingConnection(false), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-600 dark:text-green-400';
      case 'slow': return 'text-yellow-600 dark:text-yellow-400';
      case 'offline': return 'text-red-600 dark:text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="h-4 w-4" />;
      case 'slow': return <AlertTriangle className="h-4 w-4" />;
      case 'offline': return <AlertTriangle className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  // Функция для форматирования даты в человекочитаемый формат
  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Не указано';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Если невалидная дата, вернуть как есть
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      // Если меньше минуты назад
      if (diffMins < 1) return 'Только что';
      
      // Если меньше часа назад
      if (diffMins < 60) {
        const mins = diffMins === 1 ? 'минуту' : diffMins < 5 ? 'минуты' : 'минут';
        return `${diffMins} ${mins} назад`;
      }
      
      // Если меньше суток назад
      if (diffHours < 24) {
        const hours = diffHours === 1 ? 'час' : diffHours < 5 ? 'часа' : 'часов';
        return `${diffHours} ${hours} назад`;
      }
      
      // Если меньше недели назад
      if (diffDays < 7) {
        const days = diffDays === 1 ? 'день' : diffDays < 5 ? 'дня' : 'дней';
        return `${diffDays} ${days} назад`;
      }
      
      // Иначе показываем полную дату и время
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6 mb-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Управление Overpass API
              </CardTitle>
              <CardDescription>
                Настройка и мониторинг интеграции с OpenStreetMap через Overpass API
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsSettingsOpen(true)}
              >
                <Settings className="h-4 w-4 mr-1" />
                Настройки
              </Button>
              <Button
                variant="outline" 
                size="sm"
                onClick={handleTestConnection}
                disabled={isTestingConnection || testConnectionMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isTestingConnection ? 'animate-spin' : ''}`} />
                Тест соединения
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingStats ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : overpassStats ? (
            <div className="space-y-4">
              {/* Статистика API */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Всего запросов</div>
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{overpassStats.totalQueries}</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Среднее время ответа</div>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">{overpassStats.averageResponseTime}мс</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Успешность</div>
                  <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{overpassStats.successRate}%</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm font-medium text-muted-foreground">Последний запрос</div>
                  <div className="text-sm font-bold text-orange-600 dark:text-orange-400">{formatDateTime(overpassStats.lastQuery)}</div>
                </div>
              </div>

              {/* Статус API */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Статус сервиса</h4>
                <div className={`flex items-center gap-2 ${getStatusColor(overpassStats.apiStatus)}`}>
                  {getStatusIcon(overpassStats.apiStatus)}
                  <Badge variant={overpassStats.apiStatus === 'online' ? 'default' : 'destructive'}>
                    {overpassStats.apiStatus === 'online' ? 'В сети' : 
                     overpassStats.apiStatus === 'slow' ? 'Медленно' : 'Не доступен'}
                  </Badge>
                </div>
              </div>

              {/* Конечные точки */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Конечные точки</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Основная: </span>
                    <code className="text-xs rounded border border-border bg-muted/50 px-2 py-1 font-mono text-foreground">{overpassStats.endpoints.primary}</code>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Резервные: </span>
                    <div className="flex gap-1 flex-wrap">
                      {overpassStats.endpoints.backup.map((endpoint, index) => (
                        <code key={index} className="text-xs rounded border border-border bg-muted/50 px-2 py-1 font-mono text-foreground break-all">
                          {endpoint}
                        </code>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Лимиты запросов */}
              <div>
                <h4 className="font-medium mb-2 text-foreground">Лимиты запросов</h4>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="text-sm text-foreground">
                    <span className="text-muted-foreground">Использовано: </span>
                    <span className="font-medium tabular-nums">{overpassStats.rateLimit.current}/{overpassStats.rateLimit.max}</span>
                  </div>
                  <div className="text-sm text-foreground">
                    <span className="text-muted-foreground">Сброс: </span>
                    <span className="font-medium">{formatDateTime(overpassStats.rateLimit.resetTime)}</span>
                  </div>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-[width]"
                    style={{
                      width: `${Math.min(
                        (overpassStats.rateLimit.current / Math.max(overpassStats.rateLimit.max, 1)) * 100,
                        100,
                      )}%`,
                    }}
                  />
                </div>
              </div>

              {/* Размер логов и очистка */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h4 className="font-medium flex items-center gap-2 text-foreground">
                    <Database className="h-4 w-4" />
                    Размер логов в БД
                  </h4>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (confirm('Вы уверены, что хотите удалить все логи? Это действие нельзя отменить.')) {
                        cleanupLogsMutation.mutate();
                      }
                    }}
                    disabled={cleanupLogsMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {cleanupLogsMutation.isPending ? 'Очистка...' : 'Очистить все логи'}
                  </Button>
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="text-sm text-foreground">
                    <span className="font-medium text-muted-foreground">Общий размер: </span>
                    <span className="tabular-nums">{overpassStats.tableSize?.totalSize || '0 bytes'}</span>
                  </div>
                  <div className="mt-1 text-sm text-foreground">
                    <span className="font-medium text-muted-foreground">Размер таблицы: </span>
                    <span className="tabular-nums">{overpassStats.tableSize?.tableSize || '0 bytes'}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Нет данных о работе Overpass API
            </div>
          )}
        </CardContent>
      </Card>

      {/* Диалог настроек Overpass API */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Настройки Overpass API</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Включить Overpass API</Label>
                <p className="text-xs text-muted-foreground">Использовать Overpass API для запросов к OpenStreetMap</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Режим эндпоинтов</Label>
              <p className="text-xs text-muted-foreground mb-2">
                local — локальный Overpass в контуре; public — публичные URL (и backup)
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={(settings.endpointMode || "local") === "local" ? "default" : "outline"}
                  onClick={() =>
                    setSettings({
                      ...settings,
                      endpointMode: "local",
                      primaryEndpoint:
                        settings.localPrimaryEndpoint ||
                        settings.primaryEndpoint ||
                        "http://overpass/api/interpreter",
                      backupEndpoints: settings.localBackupEndpoints || [],
                    })
                  }
                >
                  Local
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={settings.endpointMode === "public" ? "default" : "outline"}
                  onClick={() => {
                    const publicPrimary =
                      settings.publicPrimaryEndpoint ||
                      DEFAULT_PUBLIC_PRIMARY;
                    const existing =
                      settings.publicBackupEndpoints ||
                      settings.backupEndpoints ||
                      [];
                    const backupEndpoints =
                      existing.length > 0
                        ? existing
                        : defaultPublicBackupsExcluding(publicPrimary);
                    setSettings({
                      ...settings,
                      endpointMode: "public",
                      primaryEndpoint: publicPrimary,
                      publicPrimaryEndpoint: publicPrimary,
                      backupEndpoints,
                      publicBackupEndpoints: backupEndpoints,
                    });
                  }}
                >
                  Public
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Основная конечная точка</Label>
              <Input
                className="border-border bg-background text-foreground"
                value={settings.primaryEndpoint}
                onChange={(e) => {
                  const primaryEndpoint = e.target.value;
                  const mode = settings.endpointMode || "local";
                  setSettings({
                    ...settings,
                    primaryEndpoint,
                    ...(mode === "public"
                      ? { publicPrimaryEndpoint: primaryEndpoint }
                      : { localPrimaryEndpoint: primaryEndpoint }),
                  });
                }}
                placeholder={
                  (settings.endpointMode || "local") === "public"
                    ? DEFAULT_PUBLIC_PRIMARY
                    : "http://overpass/api/interpreter"
                }
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Резервные конечные точки (по одной на строку)</Label>
                {(settings.endpointMode || "local") === "public" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      const primary =
                        settings.primaryEndpoint || DEFAULT_PUBLIC_PRIMARY;
                      const backupEndpoints =
                        defaultPublicBackupsExcluding(primary);
                      setSettings({
                        ...settings,
                        backupEndpoints,
                        publicBackupEndpoints: backupEndpoints,
                      });
                    }}
                  >
                    Подставить зеркала
                  </Button>
                )}
              </div>
              {(settings.endpointMode || "local") === "public" && (
                <p className="text-xs text-muted-foreground">
                  При недоступности основной точки система по очереди пробует зеркала.
                  Пустой список при сохранении заполнится дефолтными зеркалами на сервере.
                </p>
              )}
              <Textarea
                className="border-border bg-background font-mono text-foreground text-sm"
                value={settings.backupEndpoints.join('\n')}
                onChange={(e) => {
                  const backupEndpoints = e.target.value
                    .split("\n")
                    .filter((url) => url.trim());
                  const mode = settings.endpointMode || "local";
                  setSettings({
                    ...settings,
                    backupEndpoints,
                    ...(mode === "public"
                      ? { publicBackupEndpoints: backupEndpoints }
                      : { localBackupEndpoints: backupEndpoints }),
                  });
                }}
                placeholder={DEFAULT_PUBLIC_BACKUPS.join("\n")}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Таймаут (секунды)</Label>
                <Input
                  className="border-border bg-background text-foreground"
                  type="number"
                  value={settings.timeoutSeconds}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    timeoutSeconds: parseInt(e.target.value) || 30 
                  })}
                  min="5"
                  max="300"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Попытки повтора</Label>
                <Input
                  className="border-border bg-background text-foreground"
                  type="number"
                  value={settings.retryAttempts}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    retryAttempts: parseInt(e.target.value) || 3 
                  })}
                  min="0"
                  max="10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Лимит запросов в минуту</Label>
                <Input
                  className="border-border bg-background text-foreground"
                  type="number"
                  value={settings.rateLimitPerMinute}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    rateLimitPerMinute: parseInt(e.target.value) || 10 
                  })}
                  min="1"
                  max="60"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Время жизни кэша (минуты)</Label>
                <Input
                  className="border-border bg-background text-foreground"
                  type="number"
                  value={settings.cacheTtlMinutes}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    cacheTtlMinutes: parseInt(e.target.value) || 60 
                  })}
                  min="1"
                  max="1440"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Использовать сжатие</Label>
                  <p className="text-xs text-muted-foreground">Включить gzip сжатие для ускорения передачи данных</p>
                </div>
                <Switch
                  checked={settings.useCompression}
                  onCheckedChange={(checked) => setSettings({ ...settings, useCompression: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Оптимизация запросов</Label>
                  <p className="text-xs text-muted-foreground">Автоматически оптимизировать Overpass QL запросы</p>
                </div>
                <Switch
                  checked={settings.queryOptimization}
                  onCheckedChange={(checked) => setSettings({ ...settings, queryOptimization: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Кэширование результатов</Label>
                  <p className="text-xs text-muted-foreground">Сохранять результаты запросов для повторного использования</p>
                </div>
                <Switch
                  checked={settings.cacheResults}
                  onCheckedChange={(checked) => setSettings({ ...settings, cacheResults: checked })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Разрешенные теги (через запятую)</Label>
              <Input
                className="border-border bg-background text-foreground"
                value={settings.allowedTags.join(', ')}
                onChange={(e) => setSettings({ 
                  ...settings, 
                  allowedTags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) 
                })}
                placeholder="building, highway, landuse, boundary"
              />
            </div>

            <div className="space-y-4">
              <Label className="text-sm font-medium">Пользовательские запросы</Label>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Здания:</Label>
                  <Textarea
                    value={settings.customQueries.buildings}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      customQueries: { ...settings.customQueries, buildings: e.target.value }
                    })}
                    rows={3}
                    className="border-border bg-background font-mono text-xs text-foreground"
                  />
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Дороги:</Label>
                  <Textarea
                    value={settings.customQueries.roads}
                    onChange={(e) => setSettings({ 
                      ...settings, 
                      customQueries: { ...settings.customQueries, roads: e.target.value }
                    })}
                    rows={3}
                    className="border-border bg-background font-mono text-xs text-foreground"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSettingsOpen(false)}>
              Отмена
            </Button>
            <Button 
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isPending}
            >
              {saveSettingsMutation.isPending ? "Сохранение..." : "Сохранить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}